import React, { useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { 
  User, 
  Building,
  CreditCard,
  Users,
  Key,
  Bell,
  Shield,
  Save,
  Upload
} from 'lucide-react';

export function AccountSettingsPage() {
  const [companyName, setCompanyName] = useState('Infervia Healthcare');
  const [facilities, setFacilities] = useState('3');
  const [beds, setBeds] = useState('2400');

  return (
    <div className="flex-1 p-6 space-y-6 overflow-auto bg-background">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold mb-2">Account Settings</h1>
        <p className="text-muted-foreground">
          Manage your organization and team settings
        </p>
      </div>

      {/* Organization Profile */}
      <Card className="p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="p-2 bg-blue-100 rounded-lg">
            <Building className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold">Organization Profile</h2>
            <p className="text-sm text-muted-foreground">
              Update your organization information
            </p>
          </div>
        </div>

        <div className="flex items-start space-x-6 mb-6">
          <div className="flex-shrink-0">
            <div className="w-24 h-24 bg-primary/10 rounded-lg flex items-center justify-center border-2 border-dashed border-primary/30">
              <Building className="w-10 h-10 text-primary/50" />
            </div>
            <Button variant="outline" size="sm" className="w-full mt-2">
              <Upload className="w-4 h-4 mr-2" />
              Upload Logo
            </Button>
          </div>

          <div className="flex-1 space-y-4">
            <div>
              <Label htmlFor="companyName">Organization Name</Label>
              <Input 
                id="companyName" 
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                className="mt-2" 
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="facilities">Number of Facilities</Label>
                <Input 
                  id="facilities" 
                  value={facilities}
                  onChange={(e) => setFacilities(e.target.value)}
                  className="mt-2" 
                />
              </div>
              <div>
                <Label htmlFor="beds">Total Beds</Label>
                <Input 
                  id="beds" 
                  value={beds}
                  onChange={(e) => setBeds(e.target.value)}
                  className="mt-2" 
                />
              </div>
            </div>

            <div>
              <Label htmlFor="industry">Industry Type</Label>
              <select 
                id="industry"
                className="w-full mt-2 px-3 py-2 border border-border rounded-lg bg-background"
              >
                <option>Hospital/Health System</option>
                <option>Ambulatory Surgery Center</option>
                <option>Long-Term Care Facility</option>
                <option>FQHC</option>
                <option>Clinic/Medical Group</option>
              </select>
            </div>
          </div>
        </div>

        <Button>
          <Save className="w-4 h-4 mr-2" />
          Save Changes
        </Button>
      </Card>

      {/* Subscription & Billing */}
      <Card className="p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="p-2 bg-green-100 rounded-lg">
            <CreditCard className="w-5 h-5 text-green-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold">Subscription & Billing</h2>
            <p className="text-sm text-muted-foreground">
              Manage your plan and billing information
            </p>
          </div>
        </div>

        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-semibold text-lg">Professional Plan</h3>
              <p className="text-sm text-muted-foreground">Active since January 2025</p>
            </div>
            <Badge className="bg-green-100 text-green-700">Active</Badge>
          </div>
          
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div>
              <p className="text-sm text-muted-foreground">Monthly Cost</p>
              <p className="text-2xl font-bold">$800</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Next Billing Date</p>
              <p className="text-lg font-semibold">Dec 17, 2025</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Users</p>
              <p className="text-lg font-semibold">Unlimited</p>
            </div>
          </div>

          <div className="flex space-x-3">
            <Button variant="outline">
              Change Plan
            </Button>
            <Button variant="outline">
              Update Payment Method
            </Button>
            <Button variant="outline">
              View Invoices
            </Button>
          </div>
        </div>

        <div className="space-y-3">
          <h3 className="font-semibold">Current Features</h3>
          <div className="grid grid-cols-2 gap-3">
            <div className="flex items-center space-x-2 text-sm">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              <span>Federal Register Monitoring</span>
            </div>
            <div className="flex items-center space-x-2 text-sm">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              <span>HHS Documents</span>
            </div>
            <div className="flex items-center space-x-2 text-sm">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              <span>Compliance Deadline Tracking</span>
            </div>
            <div className="flex items-center space-x-2 text-sm">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              <span>Weekly Summaries</span>
            </div>
            <div className="flex items-center space-x-2 text-sm">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              <span>Impact by Department</span>
            </div>
            <div className="flex items-center space-x-2 text-sm">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              <span>Unlimited Users</span>
            </div>
          </div>
        </div>
      </Card>

      {/* Team Members */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Users className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold">Team Members</h2>
              <p className="text-sm text-muted-foreground">
                Manage user access and permissions
              </p>
            </div>
          </div>
          <Button>
            <User className="w-4 h-4 mr-2" />
            Invite User
          </Button>
        </div>

        <div className="space-y-3">
          {[
            { name: 'Sarah Johnson', email: 'sarah.johnson@infervia.com', role: 'Admin', status: 'Active' },
            { name: 'Michael Chen', email: 'michael.chen@infervia.com', role: 'Member', status: 'Active' },
            { name: 'Emily Rodriguez', email: 'emily.rodriguez@infervia.com', role: 'Member', status: 'Active' },
            { name: 'David Thompson', email: 'david.thompson@infervia.com', role: 'Viewer', status: 'Pending' }
          ].map((member, index) => (
            <div key={index} className="flex items-center justify-between p-4 bg-secondary/30 rounded-lg">
              <div className="flex items-center space-x-4">
                <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center text-white font-semibold">
                  {member.name.split(' ').map(n => n[0]).join('')}
                </div>
                <div>
                  <p className="font-medium">{member.name}</p>
                  <p className="text-sm text-muted-foreground">{member.email}</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <Badge variant="outline">{member.role}</Badge>
                <Badge className={member.status === 'Active' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}>
                  {member.status}
                </Badge>
                <Button variant="ghost" size="sm">Edit</Button>
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* API Access */}
      <Card className="p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="p-2 bg-orange-100 rounded-lg">
            <Key className="w-5 h-5 text-orange-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold">API Access</h2>
            <p className="text-sm text-muted-foreground">
              Manage API keys and integrations
            </p>
          </div>
        </div>

        <div className="p-4 bg-secondary/30 rounded-lg mb-4">
          <div className="flex items-center justify-between mb-2">
            <p className="font-medium">Production API Key</p>
            <Badge className="bg-green-100 text-green-700">Active</Badge>
          </div>
          <div className="flex items-center space-x-2">
            <code className="flex-1 px-3 py-2 bg-background border rounded font-mono text-sm">
              pk_live_••••••••••••••••••••1234
            </code>
            <Button variant="outline" size="sm">Copy</Button>
            <Button variant="outline" size="sm">Regenerate</Button>
          </div>
        </div>

        <Button variant="outline">
          <Key className="w-4 h-4 mr-2" />
          Create New API Key
        </Button>
      </Card>
    </div>
  );
}
